import { useCallback } from "react";
import { useMutation } from "@apollo/client";
import { useAuth } from "providers/auth";
import appInfo from "utility/app-info";
import { trackEvent } from "utility/mixpanel";
import { LEARNER_ACCEPT_ORI_DISCLAIMER } from "utility/graphQl/mutation";

export const ORI_DISCLAIMER_VERSION = "1.0";

interface UseOriDisclaimerReturn {
  hasAccepted: boolean;
  isAccepting: boolean;
  acceptDisclaimer: () => Promise<void>;
}

const useOriDisclaimer = (): UseOriDisclaimerReturn => {
  const { learner, updateLearner } = useAuth();
  const [acceptMutation, { loading: isAccepting }] = useMutation(
    LEARNER_ACCEPT_ORI_DISCLAIMER
  );

  const hasAccepted = learner?.oriDisclaimerAccepted ?? false;

  const acceptDisclaimer = useCallback(async () => {
    if (isAccepting || hasAccepted) return;

    try {
      await acceptMutation({
        variables: { appInfo, version: ORI_DISCLAIMER_VERSION },
      });
      updateLearner({ oriDisclaimerAccepted: true });
      trackEvent("Ori Disclaimer Accepted", { Version: ORI_DISCLAIMER_VERSION });
    } catch (error) {
      trackEvent("Ori Disclaimer Accept Failed", {
        Version: ORI_DISCLAIMER_VERSION,
        Error: error instanceof Error ? error.message : "Unknown error",
      });
      throw error;
    }
  }, [acceptMutation, hasAccepted, isAccepting, updateLearner]);

  return { hasAccepted, isAccepting, acceptDisclaimer };
};

export default useOriDisclaimer;
